import { useState, useRef, useEffect } from "react";
import { Eye, X, Send, Loader2 } from "lucide-react";
import { API } from "@/lib/apiClient";
import { trackEvent } from "@/lib/analytics";

function getSessionId() {
  let sid = localStorage.getItem("oracle_session");
  if (!sid) {
    sid = `oracle-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("oracle_session", sid);
  }
  return sid;
}

export default function OracleWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      fetch(`${API}/oracle/history/${sessionId.current}`)
        .then((r) => r.json())
        .then((h) => { if (Array.isArray(h) && h.length) setMessages(h); })
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/oracle/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line || line === "[DONE]") continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            } else if (data.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: data.error };
                return copy;
              });
            }
          } catch { /* ignore partial */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "The Oracle is resting. Please try again shortly." };
        return copy;
      });
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => { setOpen(true); trackEvent("oracle_opened"); }} data-testid="oracle-open-button"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-gold text-black pl-4 pr-5 py-3 rounded-full shadow-lg shadow-black/40 hover:bg-gold2 transition-colors group">
          <Eye size={20} strokeWidth={1.75} className="group-hover:scale-110 transition-transform" />
          <span className="text-sm font-medium">Ask the Oracle</span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm h-[32rem] flex flex-col bg-surface border border-gold/25 rounded-lg shadow-2xl shadow-black/60 overflow-hidden" data-testid="oracle-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/30">
            <div className="flex items-center gap-2">
              <Eye className="text-gold" size={18} strokeWidth={1.75} />
              <div>
                <p className="text-bone text-sm font-serif leading-none">The Oracle</p>
                <p className="text-bone/40 text-[11px] mt-0.5">Your guide to the teachings</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="oracle-close-button" className="text-bone/50 hover:text-gold transition-colors">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="oracle-messages">
            {messages.length === 0 && (
              <div className="text-center mt-8 px-2">
                <Eye className="text-gold/60 mx-auto mb-3" size={32} strokeWidth={1.25} />
                <p className="text-bone/70 text-sm leading-relaxed">
                  Greetings, seeker. Ask me anything about the teachings, teachers, or paths of knowledge on Apocalypz.org.
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-lg whitespace-pre-wrap ${
                  m.role === "user" ? "bg-gold text-black" : "bg-black/40 text-bone/90 border border-white/10"}`}>
                  {m.content || (busy && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin text-gold" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-white/10 bg-black/30">
            <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="oracle-input"
              placeholder="Ask the Oracle..." disabled={busy}
              className="flex-1 bg-black/50 border border-white/10 px-3.5 py-2.5 text-bone text-sm rounded-md focus:outline-none focus:border-gold/50 disabled:opacity-60" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="oracle-send-button"
              className="bg-gold text-black p-2.5 rounded-md hover:bg-gold2 transition-colors disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
